import { useState } from 'react';
import get from 'lodash/get';
import {
  createValuesMapFromInputs,
  evaluateFormula,
  getRequiredAttributesForCal,
  substituteValuesInFormula,
} from '../apps/genericCalculator/utils';
import { useTooltipPosition } from './useTooltipPosition';
import { PRODUCT_COUNT, QUANTITY_REQUIRED } from '../apps/genericCalculator/utils/constants';
import { InputField } from '../apps/genericCalculator/models';

export const useCalculator = ({
  calculatorConfig,
  productLevelAttributes,
  variantLevelAttributes,
  handleChangeProductCounter,
}) => {
  const {
    ui_input_fields: uiInputFields,
    calculator_helper: calculatorHelper,
    title: calculatorTitle,
    attributes: calculatorConfigAttributes,
    formulas,
    hide_calculator: hideCalculator,
    output,
    sub_title: subTitle,
  } = calculatorConfig;

  const { units: outputUnit, logo: outputLogo } = output;
  const { tooltipPosition, extraSpaceNeededForToolTip, positionTooltip } = useTooltipPosition();
  const [uiInputs, updateUiInputs] = useState(uiInputFields as InputField[]);
  const [result, updateResult] = useState({
    quantityRequired: 0,
    productCount: 1,
  });

  const resetResult = () => {
    updateResult({
      quantityRequired: 0,
      productCount: 1,
    });
    handleChangeProductCounter(1);
  };

  const updateQuantityRequired = (evaluatedValue) => {
    // quantityRequired should contain only one decimal
    const latestQuantityRequired = Math.ceil(evaluatedValue * 10) / 10 || 0;
    updateResult((prevResult) => {
      return { ...prevResult, quantityRequired: latestQuantityRequired };
    });
  };

  const updateProductCount = (evaluatedValue) => {
    const latestProductCount = Math.ceil(evaluatedValue) || 0;
    handleChangeProductCounter(latestProductCount);
    updateResult((prevResult) => {
      return { ...prevResult, productCount: latestProductCount };
    });
  };

  const calculateAndUpdateResult = (inputsRequiredForCal) => {
    const valuesMap = createValuesMapFromInputs(inputsRequiredForCal);
    for (const formulaData of formulas) {
      const formula = get(formulaData, 'formula', '');
      const formulaType = get(formulaData, 'type', '');
      const formulaWithSubstitutedValues = substituteValuesInFormula(formula, valuesMap);
      const evaluatedValue = evaluateFormula(formulaWithSubstitutedValues);
      if (!evaluatedValue) return;
      if (formulaType === QUANTITY_REQUIRED) {
        updateQuantityRequired(evaluatedValue);
      } else if (formulaType === PRODUCT_COUNT) {
        updateProductCount(evaluatedValue);
      } else {
        resetResult();
      }
    }
  };

  const processInputDataAndExecuteCalculation = (inputData) => {
    const allAttributes = [...variantLevelAttributes, ...productLevelAttributes];
    const requiredAttributes = getRequiredAttributesForCal(calculatorConfigAttributes, allAttributes);
    const areAllAttributesValueExists = inputData.every((attr) => !!get(attr, 'value', ''));
    if (areAllAttributesValueExists) {
      const inputsRequiredForCal = [...inputData, ...requiredAttributes];
      calculateAndUpdateResult(inputsRequiredForCal);
    } else {
      // reset if any value is empty
      resetResult();
    }
  };

  const updatePopOverValue = (event, showPopover, inputData) => {
    const latestUiInputs = uiInputs.map((iData) => {
      if (inputData.short_name === iData.short_name && inputData.title === iData.title) {
        return {
          ...iData,
          showPopover,
        };
      } else {
        return {
          ...iData,
          showPopover: false, // reset previous tooltip
        };
      }
    });
    updateUiInputs(latestUiInputs);
    positionTooltip(event);
  };

  const updateUiInputValue = (newValue, inputData) => {
    const latestUiInputs = uiInputs.map((iData) => {
      if (inputData.short_name === iData.short_name && inputData.title === iData.title) {
        return {
          ...iData,
          value: newValue,
          isError: false,
        };
      } else {
        return iData;
      }
    });
    updateUiInputs(latestUiInputs);
    processInputDataAndExecuteCalculation(latestUiInputs);
  };

  const updateUiInputError = (isError, inputData) => {
    const latestUiInputs = uiInputs.map((iData) => {
      if (inputData.short_name === iData.short_name && inputData.title === iData.title) {
        return {
          ...iData,
          isError,
        };
      } else {
        return iData;
      }
    });
    updateUiInputs(latestUiInputs);
  };

  const handleInputOnBlur = (event, inputData) => {
    const value = get(event, 'target.value', '');
    let isError = false;
    if (value === '') {
      isError = true;
    }
    updateUiInputError(isError, inputData);
  };

  return {
    uiInputs,
    result,
    calculatorTitle,
    calculatorHelper: { ...calculatorHelper, file: get(calculatorHelper, 'file.url', '') },
    outputUnit,
    outputLogo: get(outputLogo, 'url', ''),
    hideCalculator,
    tooltipPosition,
    extraSpaceNeededForToolTip,
    subTitle,
    updatePopOverValue,
    updateUiInputError,
    updateUiInputValue,
    handleInputOnBlur,
  };
};
